import React, { Component, ReactNode } from 'react'
import { DefaultToolRenderer } from './DefaultToolRenderer'
import type { ToolRendererProps } from './types'

interface Props {
  children: ReactNode
  fallbackProps: ToolRendererProps
}

interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary that catches rendering errors in custom tool renderers
 * and falls back to the DefaultToolRenderer with error information.
 *
 * This prevents a single broken renderer from crashing the entire chat interface.
 */
export class ToolRendererErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Tool renderer error:', {
      toolName: this.props.fallbackProps.toolName,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })
  }

  render() {
    if (this.state.hasError) {
      // Fall back to default renderer with error message
      return (
        <div className="space-y-1">
          <DefaultToolRenderer {...this.props.fallbackProps} />
          <div className="px-2 py-1 bg-magazine-coral/10 border border-magazine-coral/20 rounded text-xs">
            <p className="font-sans text-caption text-magazine-coral">
              Renderer error: {this.state.error?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
